/**
 * Who is signed in decides what is drawn. Signed out, the sign-in screen.
 * Signed in, the frame (components/AppShell.tsx) around the screens for the
 * organisation they are working in -- their own queue first, because that is
 * what most people open utuwrk for.
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import type { OrgSummary } from "@utuwrk/core";
import { AdminConsole } from "./components/AdminConsole.js";
import { AppShell } from "./components/AppShell.js";
import { AuditLog } from "./components/AuditLog.js";
import { AuditSearch } from "./components/AuditSearch.js";
import { Exports } from "./components/Exports.js";
import { EmptyState, Link, Loading, PageHeader } from "./components/bits.js";
import { Board } from "./components/Board.js";
import { CardPage } from "./components/CardPage.js";
import { Employee, Employees } from "./components/Employees.js";
import { Week } from "./components/Week.js";
import { Notices } from "./components/Notices.js";
import { Overdue } from "./components/Overdue.js";
import { Onboardings, canStartHere } from "./components/Onboardings.js";
import { People } from "./components/People.js";
import { Queue } from "./components/Queue.js";
import { Settings } from "./components/Settings.js";
import { Setup } from "./components/Setup.js";
import { SignIn } from "./components/SignIn.js";
import { Signup } from "./components/Signup.js";
import { StartOffboarding } from "./components/StartOffboarding.js";
import { StartOnboarding } from "./components/StartOnboarding.js";
import { Summary } from "./components/Summary.js";
import { TemplatePreview } from "./components/TemplatePreview.js";
import { Templates } from "./components/Templates.js";
import { mayOpen } from "./lib/access.js";
import { orgApi } from "./lib/api.js";
import { rememberPlan, takePlan } from "./lib/signup.js";
import { OrgProvider, useOrg } from "./lib/org.js";
import { go, useLocation, type Screen } from "./lib/router.js";
import { linkToken, signInRefusal, useSession } from "./lib/session.js";
import { dayText } from "./lib/words.js";

/**
 * The template editor, loaded when it is opened: the map library is most of
 * its weight, and the people doing the work never open it.
 */
const TemplatePage = lazy(() =>
  import("./components/TemplatePage.js").then((module) => ({ default: module.TemplatePage })),
);

/** The organisation last worked in on this device, if the person is still in it. */
const ORG_KEY = "utuwrk.org";

function rememberedOrg(organizations: OrgSummary[]): OrgSummary | null {
  let saved: string | null = null;
  try {
    saved = window.localStorage.getItem(ORG_KEY);
  } catch {
    // Private windows can refuse storage; the first organisation is a fine answer.
  }
  return organizations.find((org) => org.id === saved) ?? organizations[0] ?? null;
}

// Before anything is drawn: the website's /signup?plan= has to outlive signing in.
rememberPlan(window.location);

export function App() {
  const session = useSession();
  const screen = useLocation();
  const [token, setToken] = useState(() => linkToken(window.location));
  // Read once, then out of the address bar, so a reload does not say it again.
  const [refusal] = useState(() => signInRefusal(window.location));
  useEffect(() => {
    if (refusal) go("/", true);
  }, [refusal]);
  const [leaving, setLeaving] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);

  const org =
    session.organizations.find((candidate) => candidate.id === orgId) ?? rememberedOrg(session.organizations);

  useEffect(() => {
    if (!org) return;
    try {
      window.localStorage.setItem(ORG_KEY, org.id);
    } catch {
      // See rememberedOrg.
    }
  }, [org]);

  /** Out of the address bar and the history, once it has been spent or refused. */
  const forgetToken = () => {
    setToken(null);
    go("/", true);
  };

  if (session.status === "loading") return <div className="app" aria-busy="true" />;

  if (session.status === "unconfigured" || session.status === "unreachable") {
    return (
      <main className="auth">
        <section className="panel auth-card">
          <h1 className="auth-title">
            {session.status === "unconfigured" ? "utuwrk is not set up here yet" : "utuwrk is out of reach"}
          </h1>
          <p className="problem" role="alert">
            {session.status === "unconfigured"
              ? `This deploy is missing: ${session.missing.join(", ")}.`
              : session.problem}
          </p>
          <button className="button button-quiet" type="button" onClick={() => void session.refresh()}>
            Try again
          </button>
        </section>
      </main>
    );
  }

  if (session.status === "out" || !session.user) {
    return (
      <SignIn
        token={token}
        refusal={refusal}
        signingUp={screen.name === "signup"}
        onSignedIn={session.refresh}
        onLinkSpent={forgetToken}
      />
    );
  }

  const signOut = async (everywhere: boolean) => {
    setLeaving(true);
    try {
      await session.signOut(everywhere);
    } finally {
      setLeaving(false);
    }
  };

  /**
   * A new organisation: into it, then to the plan chosen on the website if
   * there was one (Stripe sends them on to the setup wizard), else straight to
   * the wizard. A checkout that will not start leaves them on Free, in the
   * wizard, where Settings can try again.
   */
  const created = async (id: string) => {
    await session.refresh();
    setOrgId(id);
    const plan = takePlan();
    if (plan) {
      try {
        window.location.assign((await orgApi(id).checkout(plan, "setup")).url);
        return;
      } catch {
        // Paying by card may not be set up; Free is a working state.
      }
    }
    go({ name: "setup" });
  };

  const user = session.user;
  const shell = (unread: number | null, children: ReactNode) => (
    <AppShell
      user={user}
      organizations={session.organizations}
      org={org}
      onOrg={(id) => {
        setOrgId(id);
        go({ name: "queue" });
      }}
      screen={screen}
      unread={unread}
      canStart={org !== null && canStartHere(org.role)}
      leaving={leaving}
      onSignOut={(everywhere) => void signOut(everywhere)}
    >
      {children}
    </AppShell>
  );

  if (!org) {
    return shell(
      null,
      screen.name === "admin" && user.isAdmin ? (
        <AdminConsole selfId={user.id} selfEmail={user.email} />
      ) : screen.name === "preview" ? (
        <TemplatePreview />
      ) : screen.name !== "queue" && screen.name !== "signup" ? (
        // A wrong address says so, rather than looking like "you have no organisation".
        // /admin for a non-admin lands here too, on purpose: it must look like any missing page.
        <Missing />
      ) : (
        // Self-serve signup while it is open; "your admin adds you" while it is not.
        <Signup hasOrg={false} onCreated={created} />
      ),
    );
  }

  if (screen.name === "signup") return shell(null, <Signup hasOrg onCreated={created} />);

  return (
    <OrgProvider key={org.id} org={org} refreshSession={session.refresh}>
      <WithUnread>
        {(unread) =>
          shell(
            unread,
            <>
              {org.closesAt !== null ? <Closing closesAt={org.closesAt} /> : null}
              <OrgScreen screen={screen} selfId={user.id} selfEmail={user.email} isAdmin={user.isAdmin} />
            </>,
          )
        }
      </WithUnread>
    </OrgProvider>
  );
}

/**
 * On every screen while the organisation closes (PRD 13.3): when everything
 * goes, and where to take it from first.
 */
function Closing({ closesAt }: { closesAt: number }) {
  return (
    <p className="notice-line danger closing-line" role="status">
      This organisation is closing. Everything in it is deleted on{" "}
      {dayText(new Date(closesAt).toISOString().slice(0, 10), true)}. Until then it can be read, and{" "}
      <Link to={{ name: "export" }}>exported</Link>; an admin can reopen it in Settings.
    </p>
  );
}

/** The unread count lives in the organisation's context; the frame is drawn from outside it. */
function WithUnread({ children }: { children: (unread: number) => ReactNode }) {
  const { unread } = useOrg();
  return <>{children(unread)}</>;
}

function OrgScreen({
  screen,
  selfId,
  selfEmail,
  isAdmin,
}: {
  screen: Screen;
  selfId: string;
  selfEmail: string;
  isAdmin: boolean;
}) {
  const { org } = useOrg();
  // A screen the menus would not offer is not there, typed address or not.
  if (!mayOpen(screen, org.role, isAdmin, org.plan)) return <Missing />;
  switch (screen.name) {
    case "queue":
      return <Queue />;
    case "notices":
      return <Notices />;
    case "onboardings":
      return <Onboardings kind="onboarding" />;
    case "offboardings":
      return <Onboardings kind="offboarding" />;
    case "start":
      return <StartOnboarding />;
    case "leave":
      return <StartOffboarding employeeId={screen.employeeId} trigger={screen.trigger} />;
    case "board":
      return <Board id={screen.id} kind={screen.kind} />;
    case "audit":
      return <AuditLog id={screen.id} kind={screen.kind} />;
    case "summary":
      return <Summary id={screen.id} />;
    case "log":
      return <AuditSearch />;
    case "export":
      return <Exports />;
    case "card":
      return <CardPage id={screen.id} cardKey={screen.key} />;
    case "week":
      return <Week />;
    case "employees":
      return <Employees />;
    case "overdue":
      return <Overdue />;
    case "setup":
      return <Setup />;
    case "employee":
      return <Employee id={screen.id} />;
    case "people":
      return <People selfId={selfId} />;
    case "settings":
      return <Settings />;
    case "preview":
      return <TemplatePreview />;
    case "templates":
      return <Templates />;
    case "template":
      return (
        <Suspense fallback={<Loading title="Template" />}>
          <TemplatePage id={screen.id} tab={screen.tab} />
        </Suspense>
      );
    case "admin":
      return isAdmin ? <AdminConsole selfId={selfId} selfEmail={selfEmail} /> : <Missing />;
    case "missing":
      return <Missing />;
  }
}

function Missing() {
  return (
    <section className="page">
      <PageHeader title="Nothing here" />
      <EmptyState icon="alert" title="That address does not lead anywhere">
        <Link to={{ name: "queue" }}>Back to your work</Link>
      </EmptyState>
    </section>
  );
}
